import { useState } from "react";
import { Loader2, AlertCircle } from "lucide-react";
import { toast } from "react-hot-toast";
import BottomNav from "../components/BottomNav";

export default function BankTransferPage() {
  const [form, setForm] = useState({
    bankName: "",
    swiftCode: "",
    routingNumber: "",
    country: "",
    chargePriority: "OUR",
    amount: "",
    walletAddress: "",
    transactionPin: "",
  });

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleChange = (e) => {
  const { name, value } = e.target;
  setForm((prev) => ({ ...prev, [name]: value }));
};

  const handleSubmit = () => {
    const amount = parseFloat(form.amount);
    if (!form.amount || isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setLoading(true);
    setResult(null);

    setTimeout(() => {
      const swiftFee = (amount * 0.02).toFixed(2);
      setLoading(false);
      setResult({ fee: swiftFee });
    }, 2200);
  };
 

  const isValid =
    form.bankName &&
    form.swiftCode &&
    form.routingNumber &&
    form.country &&
    form.amount &&
    form.walletAddress &&
    form.transactionPin.length === 4;

  return (
    <>
      {/* Page with safe bottom padding */}
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white pt-20 pb-24 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8">

            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-primary mb-2">
                Bank Transfer Request
              </h1>
              <p className="text-gray-600">
                Fill in your bank details to initiate a withdrawal
              </p>
            </div>

            <form className="space-y-5" onSubmit={(e) => e.preventDefault()}>
              {/* Bank Name */}
              <div>
                <label htmlFor="bankName" className="block text-sm font-semibold text-gray-700 mb-1">
                  Bank Name
                </label>
                <input
                  id="bankName"
                  type="text"
                  name="bankName"
                  value={form.bankName}
                  onChange={handleChange}
                  placeholder="e.g., Chase Bank"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition"
                />
              </div>

              {/* SWIFT Code */}
              <div>
                <label htmlFor="swiftCode" className="block text-sm font-semibold text-gray-700 mb-1">
                  SWIFT / BIC Code
                </label>
                <input
                  id="swiftCode"
                  type="text"
                  name="swiftCode"
                  value={form.swiftCode}
                  onChange={handleChange}
                  placeholder="e.g., CHASUS33"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition"
                />
              </div>

              {/* Routing Number */}
              <div>
                <label htmlFor="routingNumber" className="block text-sm font-semibold text-gray-700 mb-1">
                  Routing Number
                </label>
                <input
                  id="routingNumber"
                  type="text"
                  name="routingNumber"
                  value={form.routingNumber}
                  onChange={handleChange}
                  placeholder="e.g., 021000021"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition"
                />
              </div>

              {/* Country */}
              <div>
                <label htmlFor="country" className="block text-sm font-semibold text-gray-700 mb-1">
                  Country
                </label>
                <input
                  id="country"
                  type="text"
                  name="country"
                  value={form.country}
                  onChange={handleChange}
                  placeholder="e.g., United States"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition"
                />
              </div>

              {/* Charge Priority */}
              <div>
                <label htmlFor="chargePriority" className="block text-sm font-semibold text-gray-700 mb-1">
                  Charge Priority
                </label>
                <select
                  id="chargePriority"
                  name="chargePriority"
                  value={form.chargePriority}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="OUR">OUR (Sender pays all fees)</option>
                </select>
              </div>

              {/* Amount */}
              <div>
                <label htmlFor="amount" className="block text-sm font-semibold text-gray-700 mb-1">
                  Amount (USDT)
                </label>
                <input
                  id="amount"
                  type="number"
                  name="amount"
                  value={form.amount}
                  onChange={handleChange}
                  placeholder="e.g., 1000"
                  min="1"
                  step="0.01"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition"
                />
              </div>

              {/* Wallet Address */}
              <div>
                <label htmlFor="walletAddress" className="block text-sm font-semibold text-gray-700 mb-1">
                  Your USDT Wallet Address
                </label>
                <input
                  id="walletAddress"
                  type="text"
                  name="walletAddress"
                  value={form.walletAddress}
                  onChange={handleChange}
                  placeholder="0x..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm"
                />
              </div>

              {/* Transaction PIN */}
              <div>
                <label htmlFor="transactionPin" className="block text-sm font-semibold text-gray-700 mb-1">
                  Transaction PIN
                </label>
                <input
                  id="transactionPin"
                  type="password"
                  name="transactionPin"
                  value={form.transactionPin}
                  onChange={handleChange}
                  placeholder="Enter your 4-digit PIN"
                  maxLength={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              {/* Submit Button */}
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!isValid || loading}
                className={`
                  w-full py-4 rounded-xl font-bold text-white flex items-center justify-center gap-2
                  transition-all
                  ${!isValid || loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-primary hover:bg-primary/90 shadow-lg"
                  }
                `}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing Request...
                  </>
                ) : (
                  "Submit Request"
                )}
              </button>
            </form>

            {result && (
  <div className="mt-6 p-5 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3">
    <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
    <p className="text-sm text-blue-800">
      A <strong>2% Swift fee (${result.fee})</strong> will be deducted from your withdrawal of{" "}
      <strong>${form.amount} USDT</strong>.<br />
      You will receive: <strong>${(parseFloat(form.amount) - parseFloat(result.fee)).toFixed(2)} USDT</strong>
    </p>
  </div>
)}
          </div>
        </div>
      </div>

      {/* Bottom Navigation – Uncomment when imported */}
       <BottomNav /> 
    </>
  );
}